import queryString from 'query-string'
import { EventSource } from 'eventsource'

import { apiService, fetchingAPI } from '@/api'
import { toast } from '@/components/toasts/toast.helpers'

import { buildAnalyticsParams } from './voiceAgentsAnalytics.helpers'
import {
  setLoading,
  setError,
  setSummary,
  setCallVolume,
  setByAgent,
  setRecentCalls,
  setActiveCalls,
  setActiveCallsStatus,
  ACTIVE_CALLS_STATUS,
} from './voiceAgentsAnalytics.redux'

const baseUrl = () => `${apiService.reporting}/api/voice-agents/performance`

const MSG_LOAD_FAILED = 'Failed to load analytics'

// URL helpers mirror agentProvisioning (root/orgQuery/withOrg).
const root = () => apiService.voiceAgents

const orgQuery = (orgId) => queryString.stringify({ requested_organization_id: orgId || undefined })

const withOrg = (url, orgId) => {
  const params = orgQuery(orgId)
  return params ? `${url}?${params}` : url
}

// Authed raw-Response fetch for the SSE stream (mirrors the agentProvisioning
// call shape). `init` carries EventSource's abort signal + Last-Event-ID;
// getState gives a fresh token per (re)connect attempt.
const fetchingStream = (url, init, getState) => {
  const { username, organizationid, token } = getState().currentUser
  return fetch(url, {
    ...init,
    headers: { ...init.headers, 'auth-type': 'web', username, organizationid, token },
  })
}

export const fetchVoiceAgentSummary = (filters) => async (dispatch) => {
  dispatch(setLoading({ summary: true }))
  dispatch(setError({ summary: null }))
  try {
    const params = queryString.stringify({ ...buildAnalyticsParams(filters) })
    const data = await fetchingAPI(`${baseUrl()}/summary?${params}`, 'GET', dispatch)
    dispatch(setSummary(data))
  } catch (error) {
    dispatch(setError({ summary: error.message || MSG_LOAD_FAILED }))
    toast.error('Failed to load summary metrics', { cause: error })
  } finally {
    dispatch(setLoading({ summary: false }))
  }
}

export const fetchVoiceAgentCallVolume =
  (filters, granularity = 'daily') =>
  async (dispatch) => {
    dispatch(setLoading({ callVolume: true }))
    dispatch(setError({ callVolume: null }))
    try {
      const params = queryString.stringify({ ...buildAnalyticsParams(filters), granularity })
      const data = await fetchingAPI(`${baseUrl()}/call-volume?${params}`, 'GET', dispatch)
      dispatch(setCallVolume(data))
    } catch (error) {
      dispatch(setError({ callVolume: error.message || MSG_LOAD_FAILED }))
      toast.error('Failed to load call volume', { cause: error })
    } finally {
      dispatch(setLoading({ callVolume: false }))
    }
  }

export const fetchVoiceAgentByAgent = (filters) => async (dispatch) => {
  dispatch(setLoading({ byAgent: true }))
  dispatch(setError({ byAgent: null }))
  try {
    const params = queryString.stringify({ ...buildAnalyticsParams(filters) })
    const data = await fetchingAPI(`${baseUrl()}/by-agent?${params}`, 'GET', dispatch)
    dispatch(setByAgent(data))
  } catch (error) {
    dispatch(setError({ byAgent: error.message || MSG_LOAD_FAILED }))
    toast.error('Failed to load agent performance', { cause: error })
  } finally {
    dispatch(setLoading({ byAgent: false }))
  }
}

const RECENT_CALLS_LIMIT = 500

export const fetchVoiceAgentRecentCalls = (filters) => async (dispatch) => {
  dispatch(setLoading({ recentCalls: true }))
  dispatch(setError({ recentCalls: null }))
  try {
    const params = queryString.stringify({
      ...buildAnalyticsParams(filters),
      limit: RECENT_CALLS_LIMIT,
    })
    const data = await fetchingAPI(`${baseUrl()}/recent-calls?${params}`, 'GET', dispatch)
    dispatch(setRecentCalls({ items: data?.calls, total: data?.total }))
  } catch (error) {
    dispatch(setError({ recentCalls: error.message || MSG_LOAD_FAILED }))
    toast.error('Failed to load recent calls', { cause: error })
  } finally {
    dispatch(setLoading({ recentCalls: false }))
  }
}

// Live active-call counts over SSE. `eventsource` gives us native EventSource
// semantics (auto-reconnect + Last-Event-ID) with a fetch override so we can
// send the header auth the service needs. Caller closes it via the abort signal.
export const streamActiveCalls = (orgId, signal) => (dispatch, getState) => {
  dispatch(setActiveCalls({ items: [], totalActiveCalls: 0, orgId: orgId ?? null }))
  dispatch(setActiveCallsStatus(ACTIVE_CALLS_STATUS.CONNECTING))

  const source = new EventSource(withOrg(`${root()}/voice-agents/active-calls/stream`, orgId), {
    // Pin to our own endpoint; mirrors the agentProvisioning fetch shape.
    fetch: (_, init) =>
      fetchingStream(withOrg(`${root()}/voice-agents/active-calls/stream`, orgId), init, getState),
  })

  source.onopen = () => dispatch(setActiveCallsStatus(ACTIVE_CALLS_STATUS.CONNECTED))

  source.onmessage = (event) => {
    try {
      const payload = JSON.parse(event.data)
      dispatch(
        setActiveCalls({
          items: payload?.items,
          totalActiveCalls: payload?.total_active_calls,
          orgId: orgId ?? null,
        })
      )
    } catch (error) {
      console.error('active-calls stream parse error', error, event.data)
    }
  }

  // EventSource only reconnects on network drops / clean stream ends; any non-200
  // (auth failure, 5xx) is left CLOSED. So CLOSED = give up (grey, refresh), and
  // CONNECTING = mid-reconnect (grey, still trying).
  source.onerror = () => {
    dispatch(
      setActiveCallsStatus(
        source.readyState === EventSource.CLOSED
          ? ACTIVE_CALLS_STATUS.DISCONNECTED
          : ACTIVE_CALLS_STATUS.CONNECTING
      )
    )
  }

  signal.addEventListener('abort', () => source.close(), { once: true })
}
